/**
 * @file payment.ts
 * @description Global payment constants - SINGLE SOURCE OF TRUTH
 *
 * All payment-related timing, versioning, and configuration values
 * should be defined here. Consumers should import from this module
 * rather than defining local constants.
 *
 * Task 32: Post-review centralization (Task 30 follow-up)
 *
 * @see .taskmaster/docs/research/task-32-global-payment-constants.md
 */

// ─────────────────────────────────────────────────────────────────
// SESSION STORAGE CONSTANTS
// ─────────────────────────────────────────────────────────────────

/**
 * LocalStorage key prefix for payment sessions.
 * Full key format: `${PREFIX}_${tournamentId}`
 */
export const PAYMENT_SESSION_STORAGE_KEY_PREFIX = 'tournament_join_payment_session';

/**
 * Current schema version for PaymentSessionSchema.
 * MUST be incremented on breaking changes to enable migrations.
 *
 * Version history:
 * - v1: Legacy (paymentId + createdAt only)
 * - v2: Added amount/currency/expiresAt (Task 27)
 * - v3: Added userId for user-scoped storage (Task 37.2)
 */
export const PAYMENT_SESSION_SCHEMA_VERSION = 3;

/**
 * Payment session expiry time in milliseconds.
 * Sessions older than this are automatically cleaned up.
 * Value: 15 minutes (aligns with backend payment window)
 *
 * Cleanup semantics (per Task 30):
 * - Auto-cleaned on getPaymentSession() if Date.now() > expiresAt
 * - Retained after join failures (user paid, deserves retry)
 * - Cleared only on success (200), already-joined (409), or natural expiry
 */
export const PAYMENT_SESSION_EXPIRY_MS = 15 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────
// POLLING CONSTANTS
// ─────────────────────────────────────────────────────────────────

/**
 * Base interval between payment status poll requests (ms).
 * Actual interval may increase due to backoff on errors.
 */
export const PAYMENT_POLLING_INTERVAL_MS = 2000;

/**
 * Maximum number of polling attempts before timeout.
 * With 2s interval: 60 * 2s = 2 minutes max polling time.
 */
export const PAYMENT_POLLING_MAX_ATTEMPTS = 60;

// ─────────────────────────────────────────────────────────────────
// RETRY CONSTANTS
// ─────────────────────────────────────────────────────────────────

/**
 * Maximum retry attempts for transient API errors.
 * Used by createJoinPaymentSessionWithRetry and similar functions.
 * With exponential backoff (1s, 2s, 4s) = 7s total max delay.
 */
export const PAYMENT_RETRY_BUDGET = 3;

// ─────────────────────────────────────────────────────────────────
// STATE CONSTANTS
// ─────────────────────────────────────────────────────────────────

/**
 * Payment states for the tournament join payment flow.
 */
export const PAYMENT_STATES = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  AWAITING_PAYMENT: 'awaiting_payment',
  SUCCESS: 'success',
  ERROR: 'error',
} as const);

/**
 * Type for payment state values.
 */
export type PaymentState = typeof PAYMENT_STATES[keyof typeof PAYMENT_STATES];
